// src/services/regulaHubBase.js
//
// Shared base for all regula-hub service modules. Centralizes:
//   - HUB_BASE path (routed through Kong via /api proxy)
//   - hubHeaders() — optional X-API-KEY + X-Operator-Email for admin endpoints
//   - readOperatorEmail() — reads from localStorage profile
//   - requestWithAuth — hub-safe fetch that NEVER auto-logouts on 401
//
// Auth: the hub accepts the logged-in admin's Bearer JWT (the same auth-server
// token core/auth already validate) as the PRIMARY credential — hubFetch always
// sends `Authorization: Bearer <token>`. The static X-API-KEY is now OPTIONAL:
// it is only attached when VUE_APP_REGULA_HUB_API_KEY is set (dev/local or
// service integrations). The public admin bundle must NOT embed the API key.
//
// A hub 401 still does NOT trigger the api.js auto-logout: the hub is a separate
// service, so a hub error must not wipe the session and log out ALL users
// (incident 2026-05-26). hubFetch is intentionally 401-silent for that reason.

import { logger } from "@/utils/logger";

const API_BASE = "/api";

/**
 * Base path for regula-hub endpoints.
 * Final URL: "/api/regulahub/v1/admin/..." routed by Kong.
 */
export const HUB_BASE = "/regulahub/v1";

// Timeout defensivo default: um hub que não responde não pode pendurar o
// request (o poll de auditoria só checa seu próprio timeout APÓS o fetch
// retornar). Chamadas pontuais podem sobrepor via `options.timeoutMs` — jobs
// de scrape (pull_appointments, 11 unidades × login) levam minutos e passam
// um timeout maior para o fetch não abortar no meio.
const HUB_FETCH_TIMEOUT_MS = 15000;

/**
 * Hub-safe fetch — same as requestWithAuth but WITHOUT the 401 auto-logout.
 *
 * Sends the admin's Bearer JWT as the primary credential (the hub validates it
 * against the shared auth-server key and enforces admin_level server-side). A 401
 * from the hub is NOT treated as "session expired": auto-logout on hub 401 caused
 * an incident (2026-05-26) where all users were logged out because the hub
 * returned 401 and api.js requestWithAuth wiped the token. This helper stays
 * 401-silent on purpose.
 */
async function hubFetch(url, options = {}, extraHeaders = {}) {
  const token = localStorage.getItem("token");
  const defaultHeaders = { "Content-Type": "application/json" };
  if (token) defaultHeaders["Authorization"] = `Bearer ${token}`;

  // `timeoutMs` não é prop de RequestInit — extrair para não vazar no fetch()
  // e usar como timeout do AbortController (default defensivo de 15s).
  const { timeoutMs = HUB_FETCH_TIMEOUT_MS, ...fetchOptions } = options;

  // Não mutar o objeto `options` do chamador — montar um novo.
  const mergedOptions = {
    ...fetchOptions,
    headers: {
      ...defaultHeaders,
      ...extraHeaders,
      ...(options.headers || {}),
    },
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(API_BASE + url, { ...mergedOptions, signal: controller.signal });
  } catch (err) {
    logger.error("hubFetch network error:", err.message);
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Read the operator email from localStorage profile (best-effort).
 * Hub uses this for audit trail (triggered_by, X-Operator-Email).
 */
export function readOperatorEmail() {
  try {
    const raw = localStorage.getItem("profile");
    if (raw) {
      const p = JSON.parse(raw);
      return p?.email || p?.username || null;
    }
  } catch {
    /* localStorage unavailable or invalid JSON */
  }
  return null;
}

/**
 * Extra headers for hub admin endpoints. The Bearer JWT (attached by hubFetch) is
 * the primary credential; X-API-KEY is OPTIONAL and only sent when
 * VUE_APP_REGULA_HUB_API_KEY is configured (dev/local or service integrations) —
 * never in the public admin bundle. X-Operator-Email feeds the hub audit trail.
 */
export function hubHeaders() {
  const h = {};
  const apiKey = process.env.VUE_APP_REGULA_HUB_API_KEY || "";
  if (apiKey) h["X-API-KEY"] = apiKey;
  const email = readOperatorEmail();
  if (email) h["X-Operator-Email"] = email;
  return h;
}

export { hubFetch as requestWithAuth };
